import { getLogger } from '@onefootprint/logger';
import type { ReactNode } from 'react';
import type { z } from 'zod';

// Must never throw: parse runs outside the node view's error boundary, so a throw takes the document down.
export const parseFence = <T>(raw: string, schema: z.ZodType<T>): T | null => {
  try {
    return schema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
};

// In edit mode the block writes changes back through onChange, which the node view
// serializes into the fence attribute. onChange receives the same config shape the
// paired parser produces.
export type BlockFenceEditOptions = {
  editable: boolean;
  onChange: (next: unknown) => void;
  onDelete: () => void;
  onExit?: () => void;
  // True when the block appeared under a focused editor, i.e. someone just inserted it — which is
  // when an empty block should take the caret. False on load, so a document never steals focus.
  justInserted?: boolean;
};

// Pairing each kind's parser with its renderer keeps a config type bound to the component
// that consumes it, so adding a kind is a single entry. `renderBlockFence` only ever feeds
// `render` the output of the paired `parse`, so the `never` is a safe erasure.
export type BlockFenceEntry = {
  parse: (raw: string) => unknown | null;
  render: (config: never, opts: BlockFenceEditOptions) => ReactNode;
};

export type BlockFenceRegistry = Record<string, BlockFenceEntry>;

export const isBlockFenceKind = (registry: BlockFenceRegistry, value: string) => {
  return Object.hasOwn(registry, value);
};

const { logError } = getLogger({ location: 'block-fence-registry' });

const logDroppedFields = (kind: string, raw: string, config: object) => {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return;
    const dropped = Object.keys(parsed).filter(key => !Object.hasOwn(config, key));
    if (dropped.length > 0) {
      logError('Smart block dropped unknown fields on edit', undefined, { kind, droppedKeys: dropped.join(',') });
    }
  } catch {
    // raw isn't valid JSON — parse already returned null upstream, nothing to diff.
  }
};

export const renderBlockFence = (
  registry: BlockFenceRegistry,
  kind: string,
  raw: string,
  opts: BlockFenceEditOptions,
): ReactNode | null => {
  if (!isBlockFenceKind(registry, kind)) return null;
  const entry = registry[kind];
  const config = entry.parse(raw);
  if (!config) return null;
  if (opts.editable && typeof config === 'object') logDroppedFields(kind, raw, config);
  return entry.render(config as never, opts);
};
